import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/i18n/useTranslation";
import { lazy, Suspense } from "react";
import MobileBottomNav from "@/components/mobile/MobileBottomNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import OfflineSync from "@/components/OfflineSync";

const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Results = lazy(() => import("./pages/Results"));
const Itinerary = lazy(() => import("./pages/Itinerary"));
const Summary = lazy(() => import("./pages/Summary"));
const Auth = lazy(() => import("./pages/Auth"));
const MyTrips = lazy(() => import("./pages/MyTrips"));
const MyLists = lazy(() => import("./pages/MyLists"));
const Legal = lazy(() => import("./pages/Legal"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const SharedItinerary = lazy(() => import("./pages/SharedItinerary"));
const Admin = lazy(() => import("./pages/Admin"));
const Settings = lazy(() => import("./pages/Settings"));
const Guide = lazy(() => import("./pages/Guide"));
const PlanPreview = lazy(() => import("./pages/PlanPreview"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LocalizedLanding = lazy(() => import("./pages/LocalizedLanding"));
const CountrySlugLanding = lazy(() => import("./pages/CountrySlugLanding"));
const Blog = lazy(() => import("./pages/Blog"));
const Discussion = lazy(() => import("./pages/Discussion"));
const UserProfile = lazy(() => import("./pages/UserProfile"));
const Family = lazy(() => import("./pages/Family"));
const Profile = lazy(() => import("./pages/Profile"));
const Offline = lazy(() => import("./pages/Offline"));
const Contribute = lazy(() => import("./pages/Contribute"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
    <LanguageProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <OfflineSync />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/fr" element={<LocalizedLanding />} />
                  <Route path="/en" element={<LocalizedLanding />} />
                  <Route path="/es" element={<LocalizedLanding />} />
                  <Route path="/search" element={<Index />} />
                  <Route path="/results" element={<Results />} />
                  <Route path="/itinerary" element={<ProtectedRoute><Itinerary /></ProtectedRoute>} />
                  <Route path="/summary" element={<Summary />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/my-trips" element={<MyTrips />} />
                  <Route path="/my-lists" element={<MyLists />} />
                  <Route path="/legal" element={<Legal />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/shared/:token" element={<SharedItinerary />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/guide" element={<Guide />} />
                  <Route path="/guide/:slug" element={<CountrySlugLanding />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/plan-preview" element={<PlanPreview />} />
                  <Route path="/discussion" element={<ProtectedRoute><Discussion /></ProtectedRoute>} />
                  <Route path="/family" element={<Family />} />
                  <Route path="/u/:id" element={<UserProfile />} />
                  <Route path="/u/:type/:id" element={<UserProfile />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/offline" element={<Offline />} />
                  <Route path="/contribute" element={<ProtectedRoute><Contribute /></ProtectedRoute>} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <MobileBottomNav />
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </LanguageProvider>
  </ThemeProvider>
);

export default App;
